<template>
  <div class="search-page">

    <!-- Banner -->
    <div class="banner">
      <div class="banner-content">
        <h1 class="banner-title">Online Library for<br>Green Inclusive Finance</h1>
      </div>
      <div class="banner-decoration" aria-hidden="true">
        <div class="deco-circle deco-1"></div>
        <div class="deco-circle deco-2"></div>
        <div class="deco-circle deco-3"></div>
      </div>
    </div>

    <!-- Intro -->
    <div class="intro">
      <h2 class="intro-title">About</h2>
      <p class="intro-text">
        The Online Library is the first online database dedicated to Green Inclusive Finance. The development started in 2021, as part of our partnership with
        the European Microfinance Platform (e-MFP). Since 2026, the library is hosted and maintained by HEDERA.
        <br><br>
        The objective of the library is to be both comprehensive and focused, and to be a useful repository for different types of actors with
        different levels of expertise and who are looking to answer a broad range of questions. Therefore, the publications collected include
        academic as well as policy- or practitioner-oriented publications, which can deal with concrete case studies or more abstract concepts.
      </p>
    </div>

    <div class="intro">
      <h2 class="intro-title">Search the database</h2>
      <p class="intro-text">
        Use the search bar to find papers by title or author, or expand the filters
        to narrow by topic, region, language, and more.
      </p>
    </div>

    <!-- Search box -->
    <div class="search-box">
      <div class="search-row">
        <div class="search-input-wrap">
          <i class="fa fa-search search-icon"></i>
          <input
            class="search-input"
            type="search"
            autocomplete="off"
            placeholder="Search by title or author name…"
            @input="searchTermUpdated"
            @keyup.enter="searchTermKeyPressed"
          />
        </div>
        <button
          type="button"
          class="filter-toggle"
          :class="{ active: isSwitched }"
          @click="isSwitched = !isSwitched"
        >
          <i class="fa" :class="isSwitched ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          Filters
          <span class="filter-count" v-if="activeFilterCount > 0">{{ activeFilterCount }}</span>
        </button>
      </div>

      <!-- Expandable filters -->
      <div class="filters" v-if="isSwitched">
        <div class="filter-grid">
          <div class="filter-item">
            <label class="filter-label">Category</label>
            <multiselect
              v-model="searchCategory"
              :options="getOptions(categoryOptions)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="All categories"
              label="name"
              track-by="value"
              :preselect-first="false"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">Topic</label>
            <multiselect
              v-model="searchTopic"
              :options="getOptions(topicOptions)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="All topics"
              label="name"
              track-by="value"
              :preselect-first="false"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">Region</label>
            <multiselect
              v-model="searchRegion"
              :options="getOptions(regionOptions)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="All regions"
              label="name"
              track-by="value"
              :preselect-first="false"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">Language</label>
            <multiselect
              v-model="searchLanguage"
              :options="getOptions(languageOptions)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="All languages"
              label="name"
              track-by="value"
              :preselect-first="false"
            />
          </div>
        </div>

        <div class="filter-bottom">
          <div class="filter-item filter-year">
            <label class="filter-label">Year</label>
            <input
              class="year-input"
              v-model="searchYear"
              placeholder="e.g. 2022"
              type="number"
              min="1990"
              max="2099"
            />
          </div>
          <button type="button" class="reset-btn" @click="resetFilters">
            <i class="fa fa-times"></i> Reset filters
          </button>
        </div>
      </div>
    </div>

    <!-- Results meta -->
    <div class="results-meta">
      <span class="results-count">
        Found <strong>{{ allPapers.length }}</strong>
        {{ allPapers.length === 1 ? 'result' : 'results' }}
      </span>
    </div>

    <!-- Results table -->
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th class="col-authors">Authors (Year)</th>
            <th class="col-title">Title</th>
            <th class="col-category">Category</th>
            <th class="col-topics">Topics</th>
            <th class="col-region">Region</th>
            <th class="col-lang">Language</th>
          </tr>
        </thead>
        <tbody v-if="allPapers.length > 0">
          <tr v-for="paper in allPapers" :key="paper.id">
            <td class="col-authors authors-cell">{{ getAuthors(paper) }} ({{ paper.year }})</td>
            <td class="col-title title-cell">
              <button class="title-btn" @click="openDrawer(paper.id)">
                {{ paper.title }}
              </button>
            </td>
            <td class="col-category">
              <span class="badge badge-category" v-if="categoryOptions[paper.category]">
                {{ categoryOptions[paper.category] }}
              </span>
            </td>
            <td class="col-topics topics-cell">
              <span
                class="badge badge-topic"
                v-for="topic in paper.mainTopics"
                :key="topic"
              >
                {{ topicOptions[topic] }}
              </span>
            </td>
            <td class="col-region">{{ regionOptions[paper.region] }}</td>
            <td class="col-lang">
              <span class="badge badge-lang" v-if="languageOptions[paper.language]">
                {{ languageOptions[paper.language] }}
              </span>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6" class="empty-state">
              <i class="fa fa-search"></i>
              No results found. Try adjusting your search or filters.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paper detail drawer -->
    <paper-drawer
      :paper-id="selectedPaperId"
      @close="closeDrawer"
    />

  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import PaperDrawer from '@/components/Papers/PaperDrawer'
import { PAPER_SEARCH_QUERY } from '@/graphql'
import { topicOptions, categoryOptions, languageOptions, regionOptions } from '@/options'

export default {
  name: 'Search',
  components: { Multiselect, PaperDrawer },
  data () {
    return {
      allPapers: [],
      searchTerm: '',
      searchCategory: [],
      searchTopic: [],
      searchYear: '',
      searchRegion: [],
      searchLanguage: [],
      isSwitched: false,
      selectedPaperId: null,
      categoryOptions,
      languageOptions,
      regionOptions,
      topicOptions
    }
  },
  computed: {
    activeFilterCount () {
      return (
        this.searchCategory.length +
        this.searchTopic.length +
        this.searchRegion.length +
        this.searchLanguage.length +
        (this.searchYear ? 1 : 0)
      )
    }
  },
  apollo: {
    allPapers: {
      query () {
        return PAPER_SEARCH_QUERY
      },
      variables () {
        return {
          title: this.searchTerm,
          category: this.searchCategory && this.searchCategory.map(e => e.value),
          topic: this.searchTopic && this.searchTopic.map(e => e.value),
          year: parseInt(this.searchYear),
          region: this.searchRegion && this.searchRegion.map(e => e.value),
          language: this.searchLanguage && this.searchLanguage.map(e => e.value)
        }
      },
      update (data) {
        return data.allPapers
      },
      error (errorDetails) {
        if (
          errorDetails.message.includes('Authentication Failure') ||
          errorDetails.message.includes('Signature has expired')
        ) {
          localStorage.removeItem('app-token')
        }
      }
    }
  },
  methods: {
    searchTermKeyPressed (keyVal) {
      if (keyVal.key === 'Enter') {
        this.searchTerm = keyVal.target.value
      }
    },
    searchTermUpdated (e) {
      this.searchTerm = e.target.value
    },
    getAuthors (paper) {
      if (paper.authors && paper.authors.length > 0) {
        const names = paper.authors.map(a => a.name)
        if (names.length > 2) return names[0] + ' et al.'
        return names.join(', ')
      }
      return '—'
    },
    getOptions (optionsDict) {
      return Object.keys(optionsDict).map(key => ({
        name: optionsDict[key],
        value: key
      }))
    },
    resetFilters () {
      this.searchYear = ''
      this.searchCategory = []
      this.searchTopic = []
      this.searchRegion = []
      this.searchLanguage = []
    },
    openDrawer (id) {
      this.selectedPaperId = id
    },
    closeDrawer () {
      this.selectedPaperId = null
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

/* ── Page shell ── */
.search-page {
  font-family: 'Poppins', sans-serif;
  padding: 2rem 2rem 4rem;
  max-width: 1100px;
}

/* ── Banner ── */
.banner {
  background-color: #1b3d2f;
  border-radius: 12px;
  padding: 4rem 2.5rem 3.5rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
}

.banner-content {
  position: relative;
  z-index: 2;
  max-width: 620px;
}

.banner-title {
  color: #ffffff;
  font-size: 48px;
  font-weight: 800;
  line-height: 1.2;
}

.banner-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #74c69d;
  margin-bottom: 10px;
}

.banner {
  background-image: url('../../assets/banner.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
}
.banner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(27, 61, 47, 0.82);
  border-radius: 12px;
  z-index: 1;
}
.banner-content, .banner-decoration {
  position: relative;
  z-index: 2;
}

.banner-sub {
  font-size: 13px;
  color: #9bbfae;
  line-height: 1.7;
  max-width: 540px;
}

/* Decorative circles */
.banner-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 260px;
  height: 100%;
  z-index: 1;
}

.deco-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.deco-1 {
  width: 200px;
  height: 200px;
  top: -60px;
  right: -40px;
}

.deco-2 {
  width: 140px;
  height: 140px;
  top: 20px;
  right: 100px;
}

.deco-3 {
  width: 80px;
  height: 80px;
  bottom: -20px;
  right: 60px;
}

/* ── Intro ── */
.intro {
  margin-bottom: 1.75rem;
}

.intro-title {
  font-size: 16px;
  font-weight: 700;
  color: #1b3d2f;
  margin-bottom: 4px;
}

.intro-text {
  font-size: 13px;
  color: #5a6e62;
  line-height: 1.7;
}

/* ── Search box card ── */
.search-box {
  background: #ffffff;
  border: 1px solid #d5e8db;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
}

.search-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-input-wrap {
  flex: 1;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7aaa8d;
  font-size: 13px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 42px;
  border: 1px solid #c8dbd3;
  border-radius: 7px;
  padding: 0 14px 0 38px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #1b3d2f;
  background: #f7fbf8;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}

.search-input:focus {
  border-color: #2d6a4f;
  background: #ffffff;
}

.filter-toggle {
  height: 42px;
  padding: 0 16px;
  border: 1px solid #c8dbd3;
  border-radius: 7px;
  background: #ffffff;
  color: #2c5542;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.filter-toggle:hover {
  background: #f0f7f2;
}

.filter-toggle.active {
  background: #1b3d2f;
  color: #ffffff;
  border-color: #1b3d2f;
}

.filter-count {
  background: #74c69d;
  color: #1b3d2f;
  font-size: 10px;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Filters panel ── */
.filters {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #eaf2ec;
}

.filter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-label {
  font-size: 10px;
  font-weight: 700;
  color: #7aaa8d;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.filter-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.filter-year {
  width: 140px;
}

.year-input {
  height: 38px;
  border: 1px solid #c8dbd3;
  border-radius: 6px;
  padding: 0 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #1b3d2f;
  background: #f7fbf8;
  outline: none;
  width: 100%;
  transition: border-color 0.15s;
}

.year-input:focus {
  border-color: #2d6a4f;
  background: #fff;
}

.reset-btn {
  height: 38px;
  padding: 0 16px;
  border: 1px solid #c8dbd3;
  border-radius: 6px;
  background: #ffffff;
  color: #5a6e62;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #f5f5f5;
  color: #333;
}

/* ── Results meta ── */
.results-meta {
  margin-bottom: 10px;
}

.results-count {
  font-size: 12px;
  color: #5a6e62;
  font-weight: 500;
}

.results-count strong {
  color: #1b3d2f;
  font-weight: 700;
}

/* ── Table ── */
.table-wrap {
  background: #ffffff;
  border: 1px solid #d5e8db;
  border-radius: 10px;
  overflow: hidden;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Poppins', sans-serif;
  table-layout: fixed;
}

/* Column widths */
.col-id      { width: 52px; }
.col-authors  { width: 170px; }
.col-title    { width: auto; }
.col-category { width: 110px; }
.col-topics   { width: 160px; }
.col-region   { width: 90px; }
.col-lang     { width: 90px; }

thead tr {
  background: #1b3d2f;
}

thead th {
  padding: 11px 14px;
  font-size: 10px;
  font-weight: 700;
  color: #c8dbd3;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
}

tbody tr {
  border-bottom: 1px solid #eaf2ec;
  transition: background 0.1s;
}

tbody tr:last-child {
  border-bottom: none;
}

tbody tr:hover {
  background: #f4fbf6;
}

td {
  padding: 10px 14px;
  font-size: 12.5px;
  color: #2c3e35;
  vertical-align: top;
}

.id-cell {
  color: #aab8b0;
  font-size: 11px;
  font-weight: 500;
}

.authors-cell {
  font-style: italic;
  color: #5a6e62;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-cell {
  white-space: normal;
  line-height: 1.45;
}

.title-cell a,
.title-btn {
  color: #1b3d2f;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  text-align: left;
  line-height: 1.45;
  display: block;
  width: 100%;
  min-height: 40px;
}

.title-cell a:hover,
.title-btn:hover {
  color: #2d6a4f;
  text-decoration: underline;
}

.topics-cell {
  white-space: normal;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

/* ── Badges ── */
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-category {
  background: #eaf3de;
  color: #3b6d11;
}

.badge-topic {
  background: #f0f9f4;
  color: #2d6a4f;
  border: 1px solid #c8dbd3;
}

.badge-lang {
  background: #e6f1fb;
  color: #185fa5;
}

/* ── Empty state ── */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #7aaa8d;
  font-size: 13px;
}

.empty-state i {
  display: block;
  font-size: 24px;
  margin-bottom: 10px;
  opacity: 0.4;
}

/* ── Multiselect overrides ── */
.multiselect__tags {
  border: 1px solid #c8dbd3 !important;
  border-radius: 6px !important;
  background: #f7fbf8 !important;
  min-height: 38px !important;
  padding: 4px 40px 0 8px !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 12px !important;
}

.multiselect__input,
.multiselect__single {
  font-family: 'Poppins', sans-serif !important;
  font-size: 12px !important;
  color: #1b3d2f !important;
  background: transparent !important;
}

.multiselect__placeholder {
  color: #aab8b0 !important;
  font-size: 12px !important;
  font-family: 'Poppins', sans-serif !important;
}

.multiselect__option--highlight {
  background: #2d6a4f !important;
  color: #fff !important;
}

.multiselect__option--selected {
  background: #eaf3de !important;
  color: #3b6d11 !important;
  font-weight: 600 !important;
}

.multiselect__option--selected.multiselect__option--highlight {
  background: #1b3d2f !important;
  color: #fff !important;
}

.multiselect__tag {
  background: #2d6a4f !important;
  color: #fff !important;
  border-radius: 20px !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 11px !important;
}

.multiselect__tag-icon::after {
  color: #c8dbd3 !important;
}

.multiselect__tag-icon:hover {
  background: #1b3d2f !important;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .search-page {
    padding: 1rem 1rem 3rem;
  }

  .banner {
    padding: 1.75rem 1.5rem;
    border-radius: 8px;
  }

  .banner-title {
    font-size: 20px;
  }

  .banner-decoration {
    display: none;
  }

  .search-row {
    flex-wrap: wrap;
  }

  .filter-toggle {
    width: 100%;
    justify-content: center;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-bottom {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-year {
    width: 100%;
  }

  .reset-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Papers/Search.vue